import { isRequestCancelledError } from '@/helpers/errors';
import type {
  COMMUNITY_KIND,
  CaminoCommunitySyncStartResponse,
  CaminoCommunitySyncStatusResponse,
  CommunityCardDetails,
  CommunityCardLookup,
  CommunityGlobalCardsListFiltersData,
  CommunityImportStartResponse,
  CommunityImportStatusResponse,
  CommunityListDetails,
  CommunityListLookup,
  CommunityListsFiltersData,
  CreateCommunityCardInListPayload,
  CreateCommunityListPayload,
  CurrentCommunityImportResponse,
  PagedListResponse,
  RemoveCommunityCardsFromListPayload,
  UpdateCommunityCardPayload,
  UpdateCommunityListPayload,
} from '@/types';
import apiService from './api';

type GetCurrentListImportParams = {
  churchId: string;
  listId: string;
  signal?: AbortSignal;
};

type GetListImportStatusParams = {
  churchId: string;
  listId: string;
  processId: string;
  signal?: AbortSignal;
};

class CommunityService {
  getLists = async (churchId: string, filters: CommunityListsFiltersData) => {
    try {
      const response = await apiService.get<PagedListResponse<CommunityListLookup>>(
        `/admin/church/${churchId}/community/lists`,
        {
          params: filters,
        }
      );
      return response;
    } catch (e) {
      console.error('Error fetching community lists:', e);
      throw e;
    }
  };

  getList = async (churchId: string, listId: string) => {
    try {
      const response = await apiService.get<CommunityListDetails>(
        `/admin/church/${churchId}/community/lists/${listId}`
      );
      return response.data;
    } catch (e) {
      console.error('Error fetching community list details:', e);
      throw e;
    }
  };

  createList = async (churchId: string, data: CreateCommunityListPayload) => {
    try {
      const response = await apiService.post<CommunityListDetails>(`/admin/church/${churchId}/community/lists`, data);
      return response.data;
    } catch (e) {
      console.error('Error creating community list:', e);
      throw e;
    }
  };

  updateList = async (churchId: string, listId: string, data: UpdateCommunityListPayload) => {
    try {
      const response = await apiService.put<CommunityListDetails>(
        `/admin/church/${churchId}/community/lists/${listId}`,
        data
      );
      return response.data;
    } catch (e) {
      console.error('Error updating community list:', e);
      throw e;
    }
  };

  deleteList = async (churchId: string, listId: string) => {
    try {
      await apiService.delete(`/admin/church/${churchId}/community/lists/${listId}`);
    } catch (e) {
      console.error('Error deleting community list:', e);
      throw e;
    }
  };

  getCards = async (churchId: string, filters: CommunityGlobalCardsListFiltersData) => {
    try {
      const response = await apiService.get<PagedListResponse<CommunityCardLookup>>(
        `/admin/church/${churchId}/community/cards`,
        {
          params: filters,
        }
      );
      return response;
    } catch (e) {
      console.error('Error fetching community cards:', e);
      throw e;
    }
  };

  getListCards = async (
    churchId: string,
    listId: string,
    filters?: Partial<CommunityGlobalCardsListFiltersData> & { term?: string; page?: number; limit?: number }
  ) => {
    try {
      const response = await apiService.get<PagedListResponse<CommunityCardLookup>>(
        `/admin/church/${churchId}/community/lists/${listId}/cards`,
        {
          params: filters,
        }
      );
      return response;
    } catch (e) {
      console.error('Error fetching community list cards:', e);
      throw e;
    }
  };

  getCard = async (churchId: string, cardId: string) => {
    try {
      const response = await apiService.get<CommunityCardDetails>(
        `/admin/church/${churchId}/community/cards/${cardId}`
      );
      return response.data;
    } catch (e) {
      console.error('Error fetching community card details:', e);
      throw e;
    }
  };

  getListCard = async (churchId: string, listId: string, cardId: string) => {
    try {
      const response = await apiService.get<CommunityCardDetails>(
        `/admin/church/${churchId}/community/lists/${listId}/cards/${cardId}`
      );
      return response.data;
    } catch (e) {
      console.error('Error fetching community list card details:', e);
      throw e;
    }
  };

  createListCard = async (churchId: string, listId: string, data: CreateCommunityCardInListPayload) => {
    try {
      const response = await apiService.post<CommunityCardDetails>(
        `/admin/church/${churchId}/community/lists/${listId}/cards`,
        data
      );
      return response.data;
    } catch (e) {
      console.error('Error creating community list card:', e);
      throw e;
    }
  };

  updateListCard = async (churchId: string, listId: string, cardId: string, data: UpdateCommunityCardPayload) => {
    try {
      const response = await apiService.put<CommunityCardDetails>(
        `/admin/church/${churchId}/community/lists/${listId}/cards/${cardId}`,
        data
      );
      return response.data;
    } catch (e) {
      console.error('Error updating community list card:', e);
      throw e;
    }
  };

  updateCard = async (churchId: string, cardId: string, data: UpdateCommunityCardPayload) => {
    try {
      const response = await apiService.put<CommunityCardDetails>(
        `/admin/church/${churchId}/community/cards/${cardId}`,
        data
      );
      return response.data;
    } catch (e) {
      console.error('Error updating community card:', e);
      throw e;
    }
  };

  removeListCards = async (churchId: string, listId: string, data: RemoveCommunityCardsFromListPayload) => {
    try {
      await apiService.delete(`/admin/church/${churchId}/community/lists/${listId}/cards`, {
        data,
      });
    } catch (e) {
      console.error('Error removing community cards from list:', e);
      throw e;
    }
  };

  deleteListMembers = async (churchId: string, listId: string, kind: COMMUNITY_KIND) => {
    try {
      const limit = 100;
      let page = 1;
      const cardIds: string[] = [];

      while (true) {
        const response = await this.getListCards(churchId, listId, { page, limit, kind });
        const pageData = response.data;
        cardIds.push(...pageData.items.map((card) => card.id));

        const totalPages = pageData.limit > 0 ? Math.ceil(pageData.total / pageData.limit) : 0;

        if (page >= totalPages || pageData.items.length === 0) {
          break;
        }

        page += 1;
      }

      if (cardIds.length === 0) {
        return;
      }

      await this.removeListCards(churchId, listId, { cardIds });
    } catch (e) {
      console.error('Error deleting community list members:', e);
      throw e;
    }
  };

  deleteCard = async (churchId: string, cardId: string) => {
    try {
      await apiService.delete(`/admin/church/${churchId}/community/cards/${cardId}`);
    } catch (e) {
      console.error('Error deleting community card:', e);
      throw e;
    }
  };

  startCaminoSync = async (churchId: string) => {
    try {
      const response = await apiService.post<CaminoCommunitySyncStartResponse>(
        `/admin/church/${churchId}/community/camino/sync`
      );
      return response.data;
    } catch (e) {
      console.error('Error starting Camino community sync:', e);
      throw e;
    }
  };

  getCaminoSyncStatus = async (churchId: string, signal?: AbortSignal) => {
    try {
      const response = await apiService.get<CaminoCommunitySyncStatusResponse>(
        `/admin/church/${churchId}/community/camino/sync/status`,
        { signal }
      );
      return response.data;
    } catch (e) {
      if (!isRequestCancelledError(e)) {
        console.error('Error fetching Camino community sync status:', e);
      }
      throw e;
    }
  };

  startListCaminoSync = async (churchId: string, listId: string) => {
    try {
      const response = await apiService.post<CaminoCommunitySyncStartResponse>(
        `/admin/church/${churchId}/community/lists/${listId}/camino/sync`
      );
      return response.data;
    } catch (e) {
      console.error('Error starting Camino community list sync:', e);
      throw e;
    }
  };

  getListCaminoSyncStatus = async (churchId: string, listId: string, signal?: AbortSignal) => {
    try {
      const response = await apiService.get<CaminoCommunitySyncStatusResponse>(
        `/admin/church/${churchId}/community/lists/${listId}/camino/sync/status`,
        { signal }
      );
      return response.data;
    } catch (e) {
      if (!isRequestCancelledError(e)) {
        console.error('Error fetching Camino community list sync status:', e);
      }
      throw e;
    }
  };

  // Content-Type is intentionally left to the browser so the multipart boundary is generated.
  startListImport = async (churchId: string, listId: string, csvFile: File) => {
    try {
      const formData = new FormData();
      formData.append('csv', csvFile);

      const response = await apiService.post<CommunityImportStartResponse>(
        `/admin/church/${churchId}/community/lists/${listId}/import`,
        formData
      );
      return response.data;
    } catch (e) {
      console.error('Error starting community list import:', e);
      throw e;
    }
  };

  getCurrentListImport = async ({ churchId, listId, signal }: GetCurrentListImportParams) => {
    try {
      const response = await apiService.get<CurrentCommunityImportResponse>(
        `/admin/church/${churchId}/community/lists/${listId}/import/current`,
        { signal }
      );
      return response.data;
    } catch (e) {
      if (!isRequestCancelledError(e)) {
        console.error('Error fetching current community list import:', e);
      }
      throw e;
    }
  };

  getListImportStatus = async ({ churchId, listId, processId, signal }: GetListImportStatusParams) => {
    try {
      const response = await apiService.get<CommunityImportStatusResponse>(
        `/admin/church/${churchId}/community/lists/${listId}/import/${processId}/status`,
        { signal }
      );
      return response.data;
    } catch (e) {
      if (!isRequestCancelledError(e)) {
        console.error('Error fetching community list import status:', e);
      }
      throw e;
    }
  };
}

export default new CommunityService();
